import {
  afterNextRender,
  Component,
  ElementRef,
  forwardRef,
  input,
  output,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { PayeeFormFieldComponent } from '../payee-form-field/payee-form-field.component';

@Component({
  selector: 'payee-otp-form-field',
  templateUrl: './payee-otp-form-field.component.html',
  styleUrls: ['./payee-otp-form-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PayeeOtpFormFieldComponent),
      multi: true,
    },
  ],
  imports: [PayeeFormFieldComponent, FormsModule],
})
export class PayeeOtpFormFieldComponent implements ControlValueAccessor {
  @ViewChildren('digitInput')
  private digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  readonly width = input<string>();
  readonly length = input(6);
  readonly autofocus = input(true);
  readonly completed = output<string>();

  readonly digits = Array.from({ length: this.length() }, () => signal(''));
  disabled = false;

  constructor() {
    afterNextRender(() => {
      if (this.autofocus() && !this.disabled) {
        this.focusIndex(0);
      }
    });
  }

  writeValue(value: string | null): void {
    this.digits.forEach((digit) => digit.set(''));
    this.fillFrom((value ?? '').replace(/\D/g, ''), 0);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDigitChange(index: number, value: string): void {
    const raw = value.replace(/\D/g, '');
    this.applyInput(raw, index);
  }

  onPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const raw = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!raw) {
      return;
    }
    this.applyInput(raw, index);
  }

  onBackspace(index: number, event: Event): void {
    if (this.digits[index]() || index === 0) {
      return;
    }

    event.preventDefault();
    this.fillFrom('', index - 1);
    this.emitValue();
    this.focusIndex(index - 1);
  }

  onBlur(): void {
    this.onTouched();
  }

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  private applyInput(raw: string, startIndex: number): void {
    this.fillFrom(raw, startIndex);
    const code = this.emitValue();

    if (raw.length > 0) {
      this.focusIndex(Math.min(startIndex + raw.length, this.length() - 1));
    }

    if (this.digits.every((digit) => digit())) {
      this.completed.emit(code);
    }
  }

  private fillFrom(value: string, startIndex: number): void {
    const chars = value.slice(0, this.length() - startIndex).split('');

    if (chars.length === 0) {
      chars.push('');
    }

    chars.forEach((char, offset) => {
      this.digits[startIndex + offset].set(char);
    });
  }

  private emitValue(): string {
    const code = this.digits.map((digit) => digit()).join('');
    this.onChange(code);
    return code;
  }

  private focusIndex(index: number): void {
    const el = this.digitInputs?.get(index)?.nativeElement;
    if (!el) {
      return;
    }
    el.focus();
    el.select();
  }
}
